// TMOS Target vs Actual Report Page
// Date: January 13, 2026
// Purpose: Display target vs actual report with filtering

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { businessPerformanceService, TargetVsActualFilters } from '@/services/businessPerformanceService';
import { TargetVsActual } from '@/types/businessPerformance';
import toast from 'react-hot-toast';
import {
  FunnelIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

export const TargetVsActualPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [report, setReport] = useState<TargetVsActual[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [filters, setFilters] = useState<TargetVsActualFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [planIdFilter, setPlanIdFilter] = useState<string>('');
  const [kpiIdFilter, setKpiIdFilter] = useState<string>('');

  useEffect(() => {
    loadReport();
  }, [filters]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const data = await businessPerformanceService.getTargetVsActual(filters);
      setReport(data);
    } catch (error: any) {
      console.error('Error loading target vs actual report:', error);
      toast.error(error.message || t('businessPerformance.errorLoadingReport'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadReport();
  };

  const handleFilterChange = () => {
    const newFilters: TargetVsActualFilters = {};
    if (planIdFilter) newFilters.plan_id = planIdFilter;
    if (kpiIdFilter) newFilters.kpi_id = kpiIdFilter;
    setFilters(newFilters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setPlanIdFilter('');
    setKpiIdFilter('');
    setFilters({});
    setShowFilters(false);
  };

  const handleRowClick = (item: TargetVsActual) => {
    if (item.objective_id) {
      navigate(`/performance/objectives/${item.objective_id}${item.plan_id ? `?plan_id=${item.plan_id}` : ''}`);
    }
  };

  if (loading && report.length === 0) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="flex items-center justify-center h-64">
            <div className="text-[var(--color-secondary-text)]">Loading report...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">
                {t('businessPerformance.targetVsActualReport') || 'Target vs Actual Report'}
              </h1>
              <p className="text-[var(--color-secondary-text)]">
                {t('businessPerformance.targetVsActualReportDescription') || 'Compare targets with actual performance'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <FunnelIcon className="w-4 h-4" />
                {t('common.filters') || 'Filters'}
              </Button>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2"
              >
                <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {t('common.refresh') || 'Refresh'}
              </Button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <Card variant="outline" className="mb-4">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('businessPerformance.planId') || 'Plan ID'}
                    </label>
                    <input
                      type="text"
                      value={planIdFilter}
                      onChange={(e) => setPlanIdFilter(e.target.value)}
                      placeholder={t('businessPerformance.enterPlanId') || 'Enter plan ID'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('businessPerformance.kpiId') || 'KPI ID'}
                    </label>
                    <input
                      type="text"
                      value={kpiIdFilter}
                      onChange={(e) => setKpiIdFilter(e.target.value)}
                      placeholder={t('businessPerformance.enterKpiId') || 'Enter KPI ID'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button onClick={handleFilterChange} variant="primary">
                      {t('common.apply') || 'Apply'}
                    </Button>
                    <Button onClick={clearFilters} variant="outline">
                      {t('common.clear') || 'Clear'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Report Table */}
        {report.length > 0 ? (
          <Card variant="outline">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.objective') || 'Objective'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.kpi') || 'KPI'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.target') || 'Target'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.actual') || 'Actual'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.variance') || 'Variance'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.status') || 'Status'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('common.actions') || 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.map((item, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleRowClick(item)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                          {item.objective_name || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                          {item.kpi_name || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                          {item.target_value}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                          {item.current_actual !== null ? item.current_actual.toFixed(2) : t('businessPerformance.noData') || 'No Data'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-1">
                            {item.variance >= 0 ? (
                              <>
                                <ArrowTrendingUpIcon className="w-4 h-4 text-green-600" />
                                <span className="text-green-600">
                                  +{item.variance.toFixed(2)}
                                </span>
                              </>
                            ) : (
                              <>
                                <ArrowTrendingDownIcon className="w-4 h-4 text-red-600" />
                                <span className="text-red-600">
                                  {item.variance.toFixed(2)}
                                </span>
                              </>
                            )}
                            <span className={`text-xs ml-1 ${item.variance_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ({item.variance_percent >= 0 ? '+' : ''}{item.variance_percent.toFixed(1)}%)
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs ${
                            item.trend === 'IMPROVING' ? 'bg-green-100 text-green-800' :
                            item.trend === 'DECLINING' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.trend}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.objective_id) {
                                navigate(`/performance/objectives/${item.objective_id}${item.plan_id ? `?plan_id=${item.plan_id}` : ''}`);
                              }
                            }}
                            className="flex items-center gap-1"
                          >
                            {t('common.view') || 'View'}
                            <ArrowRightIcon className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outline">
            <CardContent className="p-8 text-center">
              <p className="text-[var(--color-secondary-text)]">
                {t('businessPerformance.noDataFound') || 'No data found. Adjust filters or create objectives with targets.'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

